<template>

  <div
    class="exchange-table-widget"
    :style="{
      width: props.config.width + 'px',
      height: props.config.height + 'px',
      overflow: 'hidden'
    }"
  >
    <table
      class="exchange-table"
      :style="{
        tableLayout: 'fixed',
        overflow: 'hidden'
      }"
    >
      <thead>
        <tr :style="{ color: headerColor ,backgroundColor: config.headerBgColor || '#000000',
            fontSize: fontSize, }">
          <th
            v-for="(header, index) in headers"
            :key="index"
            :class="{ 'first-header': index === 0, 'other-header': index > 0 }"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody :style="{ color: textColor, height: availableHeight + 'px', overflow: 'hidden' }">
        <tr
          v-for="(rate, index) in paddedPagedRates"
          :key="rate.code + '-' + index + '-' + pageKey"
          :class="[{ 'even-row': index % 2 === 1 }, rowStates[index]]"
          :style="{
            height: rowHeight + 'px',
            fontSize: fontSize,
            background: props.config.tableStyle === 'alterning'
              ? (index % 2 === 0 ? (props.config.bgColorPrimary || '#ffffff') : (props.config.bgColorSecondary || '#e0e0e0'))
              : (props.config.bgColorPrimary || '#ffffff')
          }"
        >
          <td v-for="(cell, cellIdx) in getRowData(rate, index)" :key="cellIdx">
            <template v-if="cellIdx === 0">
              <img v-if="cell" :src="cell" class="flag-img" :style="{ height: flagHeight + 'px', width: flagWidth + 'px' }" />
            </template>
            <template v-else>
              {{ cell }}
            </template>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, defineProps, onBeforeUnmount, watch, nextTick } from "vue";
import { displayService } from '@/_services';

const props = defineProps<{
  headerColor: string;
  textColor: string;
  header?: Array<{ text: string }>;
  config: { 
    height: number; 
    width: number;
    transitionDuration?: string | number;
    tableStyle?: string;
    bgColorPrimary?: string;
    bgColorSecondary?: string;
    transition?: string;
  };
}>();

const rates = ref<any[]>([]);
const isOnline = ref(navigator.onLine);

const codeToFlag = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  JPY: 'jpy',
  CAD: 'ca',
  CHF: 'ch',
  AED: 'aed',
  SAR: 'sa',
  QAR: 'qa',
  KWD: 'kw',
  BHD: 'bh',
  LYD: 'lyd',
  CNY: 'cny',
};

const saveToLocalStorage = (data: any[]) => {
  try {
    localStorage.setItem('exchangeTableData', JSON.stringify(data));
    localStorage.setItem('exchangeTableDataTimestamp', Date.now().toString());
  } catch (error) {
    console.error('Error saving exchange table data to localStorage:', error);
  }
};

const loadFromLocalStorage = () => {
  try {
    const data = localStorage.getItem('exchangeTableData');
    const timestamp = localStorage.getItem('exchangeTableDataTimestamp');
    
    if (data && timestamp) {
      const parsedData = JSON.parse(data);
      const dataAge = Date.now() - parseInt(timestamp);
      
      if (dataAge < 30 * 60 * 1000) {
        return parsedData;
      }
    }
    return null;
  } catch (error) {
    console.error('Error loading exchange table data from localStorage:', error);
    return null;
  }
};

const getExchangeData = async () => {
  try {
    if (isOnline.value) {
      const response = await displayService.getExchangeData();
      const changes = response.changes || [];
      const updatedRates = await Promise.all(
        changes.map(async (item: any) => {
          let flagSrc = '';
          try {
            const code = String(item.code);
            const flagFile = (codeToFlag as Record<string, string>)[code] || code.toLowerCase();
            const flagModule = await import(`@/assets/images/flags/${flagFile}.svg`);
            flagSrc = flagModule.default;
          } catch (error) {
            flagSrc = '';
          }
          return { ...item, flagSrc };
        })
      );
      rates.value = updatedRates;
      saveToLocalStorage(updatedRates);
    } else {
      const cachedData = loadFromLocalStorage();
      if (cachedData) {
        rates.value = cachedData;
      } else {
        rates.value = [{
          code: 'SERVICE',
          buy: 0,
          sell: 0,
          flagSrc: ''
        }];
      }
    }
  } catch (err) {
    console.error("Error loading exchange data:", err);
    const cachedData = loadFromLocalStorage();
    if (cachedData) {
      rates.value = cachedData;
    } else {
      rates.value = [{
        code: 'SERVICE',
        buy: 0,
        sell: 0,
        flagSrc: ''
      }];
    }
  }
};

const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine;
  console.log(`ExchangeTable - Connection status changed: ${isOnline.value ? "Online" : "Offline"}`);
  getExchangeData();
};

const headerHeight = 40; 
const minRowHeight = 34; 
const availableHeight = computed(() => props.config.height - headerHeight);

const optimalRowsPerPage = computed(() => {
  if (rates.value.length === 0) return 1;
  
  let maxPossibleRows = Math.floor(availableHeight.value / minRowHeight);
  maxPossibleRows = Math.min(maxPossibleRows, rates.value.length);
  
  return Math.max(1, maxPossibleRows);
});

const rowHeight = computed(() => {
  return optimalRowsPerPage.value > 0 
    ? Math.floor(availableHeight.value / optimalRowsPerPage.value) 
    : minRowHeight;
});

const rowsPerPage = computed(() => optimalRowsPerPage.value);

const fontSize = computed(() => Math.max(12, Math.floor(rowHeight.value * 0.45)) + 'px');
const flagHeight = computed(() => Math.max(16, Math.floor(rowHeight.value * 0.8)));
const flagWidth = computed(() => Math.floor(flagHeight.value * 1.5));

const totalPages = computed(() => Math.ceil(rates.value.length / rowsPerPage.value));
const currentPage = ref(0);

const pagedRates = computed(() => {
  const start = currentPage.value * rowsPerPage.value;
  return rates.value.slice(start, start + rowsPerPage.value);
});

const paddedPagedRates = computed(() => {
  const page = pagedRates.value;
  const pad = Array.from({ length: rowsPerPage.value - page.length }, () => ({
    code: '',
    flagSrc: '',
    buy: '',
    sell: ''
  }));
  return [...page, ...pad];
});

const pageKey = ref(0);
const isTransitioning = ref(false);
const rowStates = ref<string[]>([]);
// Use an array instead of Set for better reactivity
const rowDataVisible = ref<boolean[]>([]);

// Function to get row data based on animation state
const getRowData = (rate: any, index: number) => {
  // During transition, only show data for rows that should be visible
  if (isTransitioning.value && !rowDataVisible.value[index]) {
    return ['', '', '', ''];
  }
  
  const formattedBuy = typeof rate.buy === 'number' ? rate.buy.toFixed(4) : rate.buy;
  const formattedSell = typeof rate.sell === 'number' ? rate.sell.toFixed(4) : rate.sell;
  
  return [rate.flagSrc, rate.code, formattedBuy, formattedSell];
};

const enterAnimationClass = computed(() => {
  switch (props.config.transition) {
    case 'rotateBack':
      return 'rotate-90-horizontal-bck';
    case 'rotateForward':
      return 'rotate-90-horizontal-fwd';
    case 'flipBack':
      return 'flip-back';
    case 'flipForward':
      return 'flip-forward';
    default:
      return '';
  }
});

const nextPage = async () => {
  if (isTransitioning.value) return;
  if (totalPages.value === 1) return;
  
  isTransitioning.value = true;
  
  if (!props.config.transition || props.config.transition === 'none') {
    currentPage.value = (currentPage.value + 1) % (totalPages.value || 1);
    pageKey.value++;
    isTransitioning.value = false;
    return;
  }
  
  //change nxt page
  currentPage.value = (currentPage.value + 1) % (totalPages.value || 1);
  pageKey.value++;
  
  await nextTick();
  
  //inti arrays for the new page
  const numRows = paddedPagedRates.value.length;
  rowStates.value = new Array(numRows).fill('hidden');
  rowDataVisible.value = new Array(numRows).fill(false);
  
  await nextTick();
  
  //animation seq
  const animateRow = (index: number) => {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        //start animation
        rowStates.value[index] = enterAnimationClass.value;
        
        //change data halfaway the animation
        setTimeout(() => {
          rowDataVisible.value[index] = true;
        }, 500); 
        
        //resolve after animation is complete
        setTimeout(resolve, 1000); // Animation duration
      }, index * 150); 
    });
  };
  
  // Start all animations in parallel but with staggered timing
  const animationPromises = [];
  for (let i = 0; i < numRows; i++) {
    animationPromises.push(animateRow(i));
  }
  
  // Wait for all animations to complete
  await Promise.all(animationPromises);
  
  // Clean up
  rowStates.value = new Array(numRows).fill('');
  rowDataVisible.value = new Array(numRows).fill(true);
  isTransitioning.value = false;
};

let pageInterval: ReturnType<typeof setInterval> | null = null;

onMounted(() => {
  updateOnlineStatus();
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  
  const fetchInterval = setInterval(() => {
    if (isOnline.value) {
      getExchangeData();
    }
  }, 30 * 60 * 1000);

  const transitionDuration = Number(props.config.transitionDuration) || 10;
  pageInterval = setInterval(nextPage, transitionDuration * 1000);

  onBeforeUnmount(() => {
    clearInterval(fetchInterval);
    if (pageInterval) clearInterval(pageInterval);
    window.removeEventListener('online', updateOnlineStatus);
    window.removeEventListener('offline', updateOnlineStatus);
  });
});

watch(() => rates.value.length, () => {
  currentPage.value = 0;
  console.log("Available height:", availableHeight.value);
  console.log("Optimal rows per page:", optimalRowsPerPage.value);
  console.log("Row height:", rowHeight.value);
  console.log("Total pages:", totalPages.value);
});

const headers = computed(() => {
  if (
    props.header &&
    Array.isArray(props.header) &&
    props.header.length > 0 &&
    !(props.header.length === 1 && props.header[0].text === "")
  ) {
    return props.header.map((h) => h.text);
  }
  return ["DEVISE", "CODE", "UNITÉ", "ACHAT", "VENTE"];
});
</script>

<script lang="ts">
export default {
  name: "ExchangeTable",
};
</script>

<style scoped>
.exchange-table-widget {
  font-family: Arial, sans-serif;
  display: flex !important;
  flex-direction: column;
  text-align: center !important;
}

.exchange-table {
  table-layout: fixed;
  border-collapse: collapse;
  text-align: left;
  display: table !important;
  width: 100%;
  height: 100%;
}

.exchange-table thead tr, .exchange-table th {
  height: 40px;
  min-height: 40px;
  max-height: 40px;
  padding: 0;
  margin: 0;
}

.exchange-table thead tr {
  border-bottom: 1px solid currentColor;
}

.exchange-table th {
  padding: 8px 1px;
  font-weight: bold;
  white-space: nowrap;
  text-transform: uppercase;
  text-align: start;
}

.first-header {
  text-align: start;
}

.other-header {
  text-align: center;
}

.exchange-table td, .exchange-table th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
 
}

.exchange-table td {
  padding: 4px 5px;
}

.devise-col {
  text-transform: uppercase;
  font-size: 0.9em;
}

.unite-col,
.achat-col,
.vente-col {
  text-align: center;
}

.code-col {
  text-align: center;
}

.flag-container {
  display: flex !important;
  align-items: center;
  gap: 8px;
}

.flag-img {
  width: auto !important;
  object-fit: contain;
}

.hidden {
  opacity: 0;
  transform: rotateX(-90deg);
  transition: none;
}

@keyframes rotate-90-horizontal-bck {
  0% {
    transform: rotateX(-90deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: rotateX(0deg);
    opacity: 1;
  }
}
.rotate-90-horizontal-bck {
  animation: rotate-90-horizontal-bck 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  backface-visibility: hidden;
  perspective: 1000px;
  transform-origin: center center;
}

@keyframes rotate-90-horizontal-fwd {
  0% { 
    transform: rotateX(90deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% { 
    transform: rotateX(0deg);
    opacity: 1;
  }
}
.rotate-90-horizontal-fwd {
  animation: rotate-90-horizontal-fwd 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  backface-visibility: hidden;
  perspective: 1000px;
  transform-origin: center center;
}

@keyframes flip-back {
  0% {
    transform: rotateY(-180deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% {
    transform: rotateY(0deg);
    opacity: 1;
  }
}
.flip-back {
  animation: flip-back 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  backface-visibility: hidden;
  perspective: 1000px;
  transform-origin: center center;
}

@keyframes flip-forward {
  0% { 
    transform: rotateY(180deg);
    opacity: 0;
  }
  50% {
    opacity: 0.5;
  }
  100% { 
    transform: rotateY(0deg);
    opacity: 1;
  }
}
.flip-forward {
  animation: flip-forward 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) both;
  backface-visibility: hidden;
  perspective: 1000px;
  transform-origin: center center;
}
</style>